import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule, ActivatedRoute } from "@angular/router";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { switchMap } from "rxjs";
import { ApiService } from "../core/api.service";
import { Order, OrderStatus } from "../core/models";

const STATUS_LABEL: Record<OrderStatus, string> = {
  submitted: "Submitted",
  confirmed: "Confirmed",
  in_production: "In production",
  ready: "Ready to ship",
  delivered: "Delivered",
};

const STATUS_PROGRESS: Record<OrderStatus, number> = {
  submitted: 20,
  confirmed: 40,
  in_production: 60,
  ready: 80,
  delivered: 100,
};

type OrderTab = "in-progress" | "warranty" | "old";

@Component({
  selector: "app-orders-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule],
  templateUrl: "./orders-page.component.html",
  styleUrls: ["./orders-page.component.scss"],
})
export class OrdersPageComponent {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly refresh = signal(0);

  readonly pageSize = 10;
  readonly search = signal(
    (this.route.snapshot.queryParamMap.get("q") ?? "").trim(),
  );
  readonly page = signal(0);

  readonly activeTab = signal<OrderTab>("in-progress");
  readonly tabs: { value: OrderTab; label: string }[] = [
    { value: "in-progress", label: "In Progress" },
    { value: "warranty", label: "Under 6-Month Warranty" },
    { value: "old", label: "Old Orders" },
  ];

  readonly orders = toSignal(
    toObservable(this.refresh).pipe(switchMap(() => this.api.getOrders())),
    { initialValue: [] as Order[] },
  );

  private isWithinWarranty(order: Order): boolean {
    if (order.status !== "delivered") return false;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);
    return new Date(order.updatedAt) >= cutoff;
  }

  readonly stats = computed(() => {
    const all = this.orders();
    const inProgress = all.filter((o) => o.status !== "delivered").length;
    return { inProgress, complete: all.length - inProgress, total: all.length };
  });

  private readonly tabFiltered = computed(() => {
    const tab = this.activeTab();
    const all = this.orders();
    if (tab === "in-progress") return all.filter((o) => o.status !== "delivered");
    if (tab === "warranty") return all.filter((o) => this.isWithinWarranty(o));
    return all.filter(
      (o) => o.status === "delivered" && !this.isWithinWarranty(o),
    );
  });

  private readonly filtered = computed(() => {
    const query = this.search().trim().toLowerCase();
    const rows = this.tabFiltered();
    if (!query) return rows;
    return rows.filter((o) =>
      [o.orderNumber, o.id, o.customerName, o.customerEmail]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  });

  readonly total = computed(() => this.filtered().length);
  readonly pageCount = computed(() =>
    Math.max(1, Math.ceil(this.filtered().length / this.pageSize)),
  );

  readonly pageRows = computed(() => {
    const page = Math.min(this.page(), this.pageCount() - 1);
    const start = page * this.pageSize;
    return this.filtered().slice(start, start + this.pageSize);
  });

  setSearch(value: string) {
    this.search.set(value);
    this.page.set(0);
  }

  setPage(delta: number) {
    this.page.set(
      Math.max(0, Math.min(this.page() + delta, this.pageCount() - 1)),
    );
  }

  selectTab(tab: OrderTab) {
    this.activeTab.set(tab);
    this.page.set(0);
  }

  statusLabel(status: OrderStatus): string {
    return STATUS_LABEL[status] ?? status;
  }

  progress(order: Order): number {
    return STATUS_PROGRESS[order.status] ?? 0;
  }

  money(value: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value ?? 0);
  }
}
